'use client'

import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import {
  Dialog, DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useCreateNotebook } from '@/lib/hooks/use-notebooks'
import { useI18n } from '@/lib/hooks/use-i18n'

const createNotebookSchema = (t: (key: string) => string) => z.object({
  name: z.string().min(1, t('createNotebookDialog.name.required')),
  description: z.string().optional(),
})

type CreateNotebookFormData = z.infer<ReturnType<typeof createNotebookSchema>>

interface CreateNotebookDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateNotebookDialog({ open, onOpenChange }: CreateNotebookDialogProps) {
  const { t } = useI18n()
  const createNotebook = useCreateNotebook()
  const schema = useMemo(() => createNotebookSchema(t), [t])

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<CreateNotebookFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
    },
  })

  const closeDialog = () => onOpenChange(false)

  const onSubmit = async (data: CreateNotebookFormData) => {
    await createNotebook.mutateAsync(data)
    closeDialog()
    reset()
  }

  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('createNotebookDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('createNotebookDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-col flex-1 min-h-0" onSubmit={handleSubmit(onSubmit)}>
        <DialogBody>
          <div className="space-y-4">
            <div className="space-y-2">
            <Label htmlFor="notebook-name">{t('createNotebookDialog.name.label')}</Label>
            <Input
              id="notebook-name"
              {...register('name')}
              placeholder={t('createNotebookDialog.name.placeholder')}
              autoFocus
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notebook-description">{t('createNotebookDialog.description.label')}</Label>
            <Textarea
              id="notebook-description"
              {...register('description')}
              placeholder={t('createNotebookDialog.description.placeholder')}
              rows={4}
              className="max-h-32 overflow-y-auto"
            />
            </div>
          </div>
          </DialogBody>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={closeDialog}>
              {t('createNotebookDialog.actions.cancel')}
            </Button>
            <Button type="submit" disabled={!isValid || createNotebook.isPending}>
              {createNotebook.isPending ? t('createNotebookDialog.actions.creating') : t('createNotebookDialog.actions.create')}
            </Button>
          </DialogFooter>
          </form>
      </DialogContent>
    </Dialog>
  )
}
